import { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Box, ClickAwayListener } from '@mui/material';
import clsx from 'clsx';
import { BUSINESS_TOOL_SECTIONS } from 'constants/businessTool';
import { useBreakpoints } from 'hooks';
import merge from 'lodash/merge';
import { useUpdateBusinessPlanChapter } from 'services/businessToolForm/hooks/useUpdateChapter';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlanChapters } from 'store/useBusinessToolStore/selectors';

import { ReactComponent as DropdownArrow } from 'assets/icons/DropdownArrow.svg';

import { useExamplePreview } from 'hooks/useExamplePreview';
import { useToggle } from 'hooks/useToggler';

import { ILanguageItem, TLanguageValue } from 'interfaces/languageItem';

import { Button } from 'components/_basic/Button/Button';

import { getLangName } from 'helpers/getBusinessDocumentLanguage';

import { useLanguageSettingsData } from './hooks/useLanguageSettingsData';

import styles from './LanguageSettings.module.scss';

export const LanguageSettings: FC = () => {
  const { i18n } = useTranslation();
  const { isAboveMobile } = useBreakpoints();
  const { isExamplePage } = useExamplePreview();

  const mutation = useUpdateBusinessPlanChapter();
  const { id: businessPlanId = '' } = useParams();
  const { GLOBAL } = useBusinessToolStore(getBusinessPlanChapters);

  const [isLanguageMenuShown, setIsLanguageMenuShown] = useToggle(false);
  const [language, setLanguage] = useState<TLanguageValue>(
    (localStorage.getItem('language') as TLanguageValue) || 'en',
  );

  const handleCloseClick = () => setIsLanguageMenuShown(false);

  const handleChooseLanguage = async (newLanguage: TLanguageValue) => {
    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
    i18n.changeLanguage(newLanguage);

    if (businessPlanId && GLOBAL && !isExamplePage) {
      const updatedValues = merge(GLOBAL, { documentLanguage: getLangName(newLanguage) });
      const body = { chapterName: BUSINESS_TOOL_SECTIONS.GLOBAL, content: JSON.stringify(updatedValues) };

      await mutation.mutateAsync({ id: businessPlanId, body });
    }
  };

  const { languageContent, getTitleFromValue } = useLanguageSettingsData();

  return (
    <Box className={styles.wrapper}>
      <Button
        fullWidth
        className={clsx(styles.languageButton, !isAboveMobile && styles.mobileStyle)}
        endIcon={<DropdownArrow />}
        onClick={setIsLanguageMenuShown}
      >
        {isAboveMobile && getTitleFromValue(language)}
      </Button>
      {isLanguageMenuShown && (
        <ClickAwayListener onClickAway={handleCloseClick}>
          <Box
            component="ul"
            className={clsx(styles.languageMenu, !isAboveMobile && styles.mobileStyle)}
            onClick={handleCloseClick}
          >
            {languageContent.map(({ title, value }: ILanguageItem) => (
              <Fragment key={title}>
                <Box component="li" className={clsx(styles.menuItem, value === language && styles.active)}>
                  <Button
                    fullWidth
                    onClick={() => handleChooseLanguage(value)}
                    className={clsx(styles.itemButton, value === language && styles.active)}
                  >
                    {title}
                  </Button>
                </Box>
              </Fragment>
            ))}
          </Box>
        </ClickAwayListener>
      )}
    </Box>
  );
};
